import { Menu, MenuProps, Button, ActionIcon } from "@mantine/core"
import { IconMessageCirclePlus } from "@tabler/icons-react"
import { CreateChatModal } from "./CreateChatModal"
import { ChatType } from "@/@client/Theasoft_DataTypes_Enums"
import { useTranslation } from "react-i18next"
import { JoinPublicGroupModal } from "./JoinPublicGroupModal"
import { HoverAndTouchTooltip } from "@/routes/app/-components/hover-and-touch-tooltip"
import { useUserCanCreateChats } from "@/orval/gen"

const chatOptions: ChatOption[] = ["Chat", "Group", "JoinGroup", "Broadcast"]

type ChatOption = "Chat" | "Group" | "JoinGroup" | "Broadcast"

type CreateOption = ChatOption | "Feed"

function getChatTypeFromOption(
  option: Omit<CreateOption, "JoinGroup">
): ChatType {
  switch (option) {
    case "Chat":
      return "Direct"
    case "Group":
      return "PrivateGroup"
    case "Broadcast":
      return "Broadcast"
    case "Feed":
      return "Feed"
    default:
      return "Direct"
  }
}

interface Props extends MenuProps {
  label?: string
  variant: "chat" | "feed"
}

export function CreateChatMenu({ variant, label, ...props }: Props) {
  const [selectedOption, setSelectedOption] = React.useState<CreateOption>()
  const { t } = useTranslation("app_server")

  const isChatVariant = variant === "chat"
  const { data: canCreateChat } = useUserCanCreateChats({
    query: { enabled: isChatVariant },
  })
  const canCreate = !isChatVariant || canCreateChat

  return (
    <>
      {isChatVariant ? (
        <Menu {...props} disabled={!canCreate}>
          <HoverAndTouchTooltip
            multiline
            disabled={canCreate}
            label={t("CantCreateChatTooltip")}
            children={
              <Menu.Target>
                {label ? (
                  <Button
                    disabled={!canCreate}
                    justify="center"
                    rightSection={<IconMessageCirclePlus />}
                  >
                    {t("NewChat")}
                  </Button>
                ) : (
                  <ActionIcon disabled={!canCreate}>
                    <IconMessageCirclePlus />
                  </ActionIcon>
                )}
              </Menu.Target>
            }
          />

          <Menu.Dropdown>
            {chatOptions.map((option) => (
              <Menu.Item key={option} onClick={() => setSelectedOption(option)}>
                {t(option)}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      ) : (
        <HoverAndTouchTooltip
          multiline
          disabled={canCreate}
          label={t("CantCreateChatTooltip")}
          children={
            label ? (
              <Button
                disabled={!canCreate}
                justify="center"
                rightSection={<IconMessageCirclePlus />}
                onClick={() => setSelectedOption("Feed")}
              >
                {t("NewFeed")}
              </Button>
            ) : (
              <ActionIcon
                disabled={!canCreate}
                onClick={() => setSelectedOption("Feed")}
              >
                <IconMessageCirclePlus />
              </ActionIcon>
            )
          }
        />
      )}
      {selectedOption && selectedOption !== "JoinGroup" ? (
        <CreateChatModal
          onClose={() => setSelectedOption(undefined)}
          opened={true}
          chatType={getChatTypeFromOption(selectedOption)}
        />
      ) : null}
      {selectedOption && selectedOption === "JoinGroup" && (
        <JoinPublicGroupModal
          onClose={() => setSelectedOption(undefined)}
          opened={selectedOption === "JoinGroup"}
        />
      )}
    </>
  )
}
